/**
 * schema.org VideoObject of the YouTube videos of a page. Since the facade (utils/youtubeFacade.ts) the player is
 * only loaded on click, so Google no longer sees an embedded player: this JSON-LD is how it still finds the videos
 * (video results, Videos tab: ~270 clicks / 90 days in Search Console, 07/10/2026).
 * Serialised by jsonLdScript ("<" escaped: names and descriptions come from Wapers' posts).
 */
export type VideoSchemaPage = {
  ids: string[]
  name?: string
  description?: string
  uploadDate?: string
}

const plainText = (value?: string) =>
  (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const useVideoSchema = (page: MaybeRefOrGetter<VideoSchemaPage>) => {
  useHead(() => {
    const { ids, name, description, uploadDate } = toValue(page)
    const title = plainText(name)
    const date = uploadDate ? new Date(uploadDate) : undefined
    if (!ids.length || !title || !date || Number.isNaN(date.getTime()))
      return {}

    const text = plainText(description).slice(0, 300) || title
    const videos = ids.map((id, index) => ({
      '@type': 'VideoObject',
      name: ids.length > 1 ? `${title} (vidéo ${index + 1})` : title,
      description: text,
      thumbnailUrl: [`https://i.ytimg.com/vi/${id}/hqdefault.jpg`],
      uploadDate: date.toISOString(),
      embedUrl: `https://www.youtube.com/embed/${id}`,
    }))

    return {
      script: [
        jsonLdScript(
          { '@context': 'https://schema.org', '@graph': videos },
          `video-schema-${ids[0]}`,
        ),
      ],
    }
  })
}
